/**
 * The small part of Markdown the chatbot writes: headings, **bold**, bullet and
 * numbered lists, a horizontal rule, [label](target) links and bare URLs. Parsed here into plain
 * data so the widget can render it with React elements -- never as HTML, so
 * nothing the model writes can inject markup.
 *
 * The text is still streaming while this runs, so anything half-written (an
 * unclosed **) simply stays as typed until the rest arrives.
 */

const HEADING = /^#{1,6}\s+(.*)$/;
const BULLET = /^\s*[-*・]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)．]\s+(.*)$/;
const RULE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;

/** Splits answer text into blocks: heading, paragraph, list, rule. */
export function parseAnswer(text) {
    const blocks = [];
    let paragraph = [];
    let list = null;

    const flush = () => {
        if (paragraph.length) blocks.push({ type: 'paragraph', lines: paragraph });
        if (list) blocks.push(list);
        paragraph = [];
        list = null;
    };

    for (const line of text.split('\n')) {
        let match;
        if (!line.trim()) {
            flush();
        } else if (RULE.test(line)) {
            flush();
            blocks.push({ type: 'rule' });
        } else if ((match = line.match(HEADING))) {
            flush();
            blocks.push({ type: 'heading', text: match[1] });
        } else if ((match = line.match(BULLET)) || (match = line.match(NUMBERED))) {
            const ordered = match.length === 3;
            const item = ordered ? match[2] : match[1];
            if (paragraph.length) {
                blocks.push({ type: 'paragraph', lines: paragraph });
                paragraph = [];
            }
            if (!list || list.ordered !== ordered) {
                if (list) blocks.push(list);
                list = { type: 'list', ordered, items: [] };
            }
            list.items.push(item);
        } else {
            if (list) {
                blocks.push(list);
                list = null;
            }
            paragraph.push(line);
        }
    }
    flush();
    return blocks;
}

// ASCII only: Japanese text runs straight on after a URL with no space, so the
// first non-ASCII character ends it. Trailing punctuation is left out too.
const URL_SOURCE = 'https?:\\/\\/[A-Za-z0-9\\-._~:/?#@!$&*+,;=%]+';
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;
// A Markdown link, [label](target), or a bare URL. The bot is told to write
// links as [label](target) so the customer reads a label, not a path (PPS-405).
const LINK_PATTERN = new RegExp(`\\[([^\\]\\n]+)\\]\\(([^)\\s]+)\\)|${URL_SOURCE}`, 'g');

// The only links that are made clickable (PPS-405): portal pages, opened in
// the same tab, and our own site, opened in a new one. Anything else -- a wrong
// or invented URL -- stays plain text, so it can never be clicked.
const PORTAL_PATH = /^\/electric(?:\/[A-Za-z0-9\-._~]+)*\/?(?:\?[A-Za-z0-9\-._~=&%]*)?$/;
const OWN_SITE = /^https:\/\/(?:[a-z0-9-]+\.)*shirokumapower\.com(?:[/?#][A-Za-z0-9\-._~:/?#@!$&*+,;=%]*)?$/;

/** 'portal', 'external' or null when the target must not be a link. */
export function linkKind(target) {
    // No '..' segments: /electric/../x would leave the portal pages.
    if (PORTAL_PATH.test(target) && !/(^|\/)\.\.?(\/|\?|$)/.test(target)) return 'portal';
    if (OWN_SITE.test(target)) return 'external';
    return null;
}

/** Splits a line into text, bold and link parts. */
export function parseInline(text) {
    const parts = [];
    text.split(/(\*\*[^*]+\*\*)/).forEach((segment) => {
        if (!segment) return;
        const bold = segment.length > 4 && segment.startsWith('**') && segment.endsWith('**');
        const body = bold ? segment.slice(2, -2) : segment;
        let last = 0;
        for (const match of body.matchAll(LINK_PATTERN)) {
            const [whole, label, target] = match;
            const raw = label ? whole : whole.replace(TRAILING_PUNCTUATION, '');
            if (match.index > last) parts.push({ text: body.slice(last, match.index), bold });
            const href = label ? target : raw;
            const kind = linkKind(href);
            // A label whose target is not allowed is still worth reading; the target is dropped.
            const shown = label ?? raw;
            parts.push(kind ? { text: shown, href, external: kind === 'external', bold } : { text: shown, bold });
            last = match.index + raw.length;
        }
        if (last < body.length) parts.push({ text: body.slice(last), bold });
    });
    return parts;
}
